import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CorrectIcon from '@material-ui/icons/Done';
import IncorrectIcon from '@material-ui/icons/Clear';
import EyeIcon from '@material-ui/icons/RemoveRedEye';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { useInterval } from '../../../tools'
import { drawP } from './functions';

const useStyles = makeStyles(theme => ({
  qa: {
    position: 'relative',
    '& h3': {
      textAlign: 'center',
      color: theme.palette.primary.main,
    },
  },
  intro: {
    textAlign: 'center',
    fontSize: '1.25em',
    margin: '0.5em 0',
    '& p': {
      margin: '0.25em 0',
      lineHeight: 1.3,
    },
    '& em': {
      fontStyle: 'normal',
      color: theme.palette.secondary.main,
    },
    '& i': {
      fontStyle: 'italic',
    },
  },
  text: {
    textAlign: 'center',
    fontSize: '1.35em',
    color: theme.palette.secondary.main,
    margin: '1em 0',
    '& svg': {
      fontSize: '1.35em',
      verticalAlign: 'middle',
    },
    '& em': {
      fontStyle: 'normal',
      color: theme.palette.secondary.main,
    },
    '& i': {
      fontStyle: 'italic',
    },
    '& p': {
      verticalAlign: 'middle',
      margin: '0.25em 0',
      lineHeight: 1.3,
    },
  },
  time: {
    textAlign: 'center',
    '& span': {
      fontSize: 30,
      verticalAlign: 'middle',
      display: 'inline-block',
      borderRadius: '50%',
      minWidth: 60,
      height: 60,
      lineHeight: 1,
      color: '#9e9e9e',
      padding: '15px 10px 0',
      background: '#eee',
    },
  },
  introAction: {
    textAlign: 'center',
    marginTop: 20,
    '& button': {
      margin: 5,
    },
  },
  closeQuiz: {
    position: 'absolute',
    top: -35,
    right: -20,
    color: '#999',
    transition: 'color 0.3s',
    cursor: 'pointer',
    '&:hover': {
      color: '#333',
    },
  },
  qPagination: {
    color: '#666',
    fontWeight: 600,
  },
  input: {
    display: 'flex',
    width: 400,
    maxWidth: '100%',
    margin: '10px auto',
    justifyContent: 'center',
    alignItems: 'center',
    '& textarea': {
      width: '100%',
      minHeight: 70,
      border: '1px solid #666',
      borderRadius: 3,
      fontSize: 16,
      textAlign: 'center',
      boxSizing: 'border-box',
      padding: '3px 12px',
      lineHeight: 1.3,
      fontFamily: theme.typography.body1.fontFamily,
    },
  },
  glance: {
    display: 'inline-block',
    padding: '0 0.5em',
    verticalAlign: 'middle',
    cursor: 'pointer',
    color: '#777',
    fontSize: 16,
    transition: 'color 0.3s',
    '&:hover': {
      color: 'green',
    },
  },
  preview: {
    display: 'inline-block',
    width: '100%',
    border: '1px solid #666',
    borderRadius: 3,
    padding: '3px 10px',
    fontSize: 16,
    textAlign: 'center',
    color: 'green',
    boxSizing: 'border-box',
    lineHeight: 1.3,
    minHeight: 70,
  },
  isCorrect: {
    display: 'inline-block',
    width: 50,
    textAlign: 'center',
  },
  quizAction: {
    textAlign: 'center',
    marginTop: 20,
    '& button': {
      margin: '0 1px',
    },
    '& > div': {
      display: 'inline-block',
      padding: '0.25em 1em',
      color: '#a1008f',
      verticalAlign: 'middle',
      margin: '0 1px',
      borderRadius: 3,
    },
  },
  '@media(max-width: 850px)': {

  }
}));

let timeout1 = 0;
let timeout2 = 0;

const endingsClean = ['.', ',', '?']

function Qa(props) {
  const classes = useStyles();
  const [no, setNo] = useState(0);
  const [time, setTime] = useState(0);
  const [preview, setPreview] = useState(false);
  const [answer, setAnswer] = useState('');
  const [testFinished, setTestFinished] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const exercise = props.data[props.state.idx];
  const data = exercise.data[no];
  const correctSentence = data.answers[0];
  const { action } = props;
  const len = exercise.data.length;
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
    // eslint-disable-next-line
  }, [no]);

  useInterval(() => {
    setTime(time + 1);
  }, testFinished ? null : 1000);

  const next = () => {
    action();
    if (no < len - 1) {
      setNo(no + 1);
      setAnswer('');
      setIsCorrect(null);
    } else {
      finishTest();
    }
  }

  const cleanString = str => {
    const lastCh = str[str.length - 1];
    if (endingsClean.indexOf(lastCh) !== -1) {
      str = str.substr(0, str.length - 1);
    }
    return str
      .replace(/  +/g, ' ')
      .trim()
      .toLowerCase();
  }

  const checkAnswer = () => {
    action();
    const ok = data.answers.find(str => cleanString(str) === cleanString(answer));
    if (ok !== undefined) {
      correct();
    } else {
      incorrect();
    }
  }

  const correct = () => {
    props.media.success();
    setIsCorrect(true);
    timeout2 = setTimeout(() => {
      next();
    }, 2000)
  }

  const incorrect = () => {
    props.media.failure();
    setIsCorrect(false);
  }

  const showPreview = () => {
    action();
    setPreview(true);
    timeout1 = setTimeout(() => {
      setPreview(false);
    }, 2000);
  }

  const handleKeyDown = (ev) => {
    action();
    if (ev.key === 'Enter') checkAnswer();
  }

  const finishTest = () => {
    setTestFinished(true);
    props.media.applause();
    props.completePart();
  }

  const onTextChange = ev => {
    let text = ev.target.value;
    setAnswer(text.replace(/[\r\n]+/gm, ""))
  }

  const pagination = (no + 1) + '/' + len;

  return (
    <div className={classes.qa}>
      <IconButton className={classes.closeQuiz} onClick={props.close}>
        <CloseIcon />
      </IconButton>
      <p className={classes.time}><span>{time}</span></p>
      <h3>{exercise.title}</h3>
      <div className={classes.intro}>{data.intro.map(drawP)}</div>
      <div className={classes.text}>{data.text.map(drawP)}</div>
      <div className={classes.input}>
        <div className={classes.glance} onClick={showPreview}><EyeIcon /></div>
        {!preview && <textarea
          type="text"
          value={answer}
          onChange={onTextChange}
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          onKeyDown={handleKeyDown}
          ref={inputRef}
        />}
        {preview && <div className={classes.preview}>{correctSentence}</div>}
        <div className={classes.isCorrect}>
          {isCorrect && <span style={{ color: 'green' }} ><CorrectIcon /></span>}
          {isCorrect === null && <span style={{ color: 'green' }}>?</span>}
          {isCorrect === false && <span style={{ color: 'red' }}><IncorrectIcon /></span>}
        </div>
      </div>
      <div className={classes.quizAction}>
        <p className={classes.qPagination}>{pagination}</p>
        {!testFinished && <Button variant="outlined" color="secondary" onClick={checkAnswer} disabled={preview || !answer}>Sprawdź</Button>}
        {testFinished && <Button variant="outlined" color="secondary" onClick={props.close}>Powrót</Button>}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    media: state.media,
    mobile: state.mobile,
  }
}

export default connect(mapStateToProps)(Qa);